import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Loader2, Star, X } from 'lucide-react';
import { cancelAppointment, fetchMyAppointments, type Appointment } from '../../api';
import { formatSalon } from '../../lib/datetime';
import ConfirmDialog from '../../components/ConfirmDialog';

import { STATUS_BADGE } from '../../lib/statusColors';

export default function MyBookings() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState<Appointment | null>(null);
  const [cancelling, setCancelling] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMyAppointments()
      .then(setAppointments)
      .catch(() => setAppointments([]))
      .finally(() => setLoading(false));
  }, []);

  const now = Date.now();
  const upcoming = appointments.filter(
    (a) => new Date(a.startTime).getTime() >= now && a.status !== 'cancelled',
  );
  const past = appointments.filter(
    (a) => new Date(a.startTime).getTime() < now || a.status === 'cancelled',
  );

  const handleCancel = async () => {
    if (!cancelTarget) return;
    const id = cancelTarget.id;
    setCancelTarget(null);
    setCancelling(id);
    setError(null);
    try {
      const updated = await cancelAppointment(id);
      setAppointments((prev) => prev.map((a) => (a.id === id ? updated : a)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't cancel this booking.");
    } finally {
      setCancelling(null);
    }
  };

  const renderCard = (appt: Appointment) => {
    const canReview = appt.status === 'completed' && !appt.hasReview;
    const canCancel =
      (appt.status === 'pending' || appt.status === 'confirmed') &&
      new Date(appt.startTime).getTime() > Date.now();
    return (
      <li key={appt.id} className="border-default bg-surface rounded-2xl border p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={`/shop/${appt.tenantSlug}`}
              className="hover:text-accent text-fg font-semibold"
            >
              {appt.tenantName}
            </Link>
            <p className="text-fg-secondary text-sm">{appt.serviceName}</p>
            <p className="text-fg-subtle mt-1 text-xs">
              {formatSalon(new Date(appt.startTime), 'EEEE, MMMM d, yyyy · h:mm a')}
            </p>
          </div>
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium capitalize ${STATUS_BADGE[appt.status]}`}
          >
            {appt.status}
          </span>
        </div>
        {canReview && (
          <Link
            to={`/shop/${appt.tenantSlug}#reviews`}
            className="text-accent mt-3 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
          >
            <Star className="h-4 w-4" />
            Leave a review
          </Link>
        )}
        {appt.status === 'completed' && appt.hasReview && (
          <p className="text-fg-subtle mt-3 text-xs">You reviewed this visit.</p>
        )}
        {canCancel && (
          <button
            onClick={() => setCancelTarget(appt)}
            disabled={cancelling === appt.id}
            className="text-danger mt-3 inline-flex items-center gap-1.5 text-sm font-medium hover:underline disabled:opacity-50"
          >
            {cancelling === appt.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <X className="h-4 w-4" />
            )}
            Cancel booking
          </button>
        )}
      </li>
    );
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-10">
      <h1 className="font-display text-fg mb-6 text-2xl font-semibold">My Bookings</h1>

      {error && (
        <div className="border-danger/25 bg-danger-soft text-danger mb-4 rounded-xl border px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-fg-subtle py-10 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin" />
        </div>
      ) : appointments.length === 0 ? (
        <div className="border-default rounded-2xl border border-dashed py-12 text-center">
          <Calendar className="text-fg-faint mx-auto mb-3 h-8 w-8" />
          <p className="text-fg-muted">You have no bookings yet.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {upcoming.length > 0 && (
            <section>
              <h2 className="text-fg-muted mb-3 text-sm font-semibold tracking-wide uppercase">
                Upcoming
              </h2>
              <ul className="space-y-3">{upcoming.map(renderCard)}</ul>
            </section>
          )}
          {past.length > 0 && (
            <section>
              <h2 className="text-fg-muted mb-3 text-sm font-semibold tracking-wide uppercase">
                Past
              </h2>
              <ul className="space-y-3">{past.map(renderCard)}</ul>
            </section>
          )}
        </div>
      )}

      <ConfirmDialog
        open={cancelTarget !== null}
        variant="danger"
        title="Cancel booking"
        message={
          cancelTarget
            ? `Cancel your booking at ${cancelTarget.tenantName}? This can't be undone.`
            : ''
        }
        confirmLabel="Cancel booking"
        cancelLabel="Keep booking"
        onConfirm={handleCancel}
        onCancel={() => setCancelTarget(null)}
      />
    </div>
  );
}
